import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { Spinner } from "../components/Spinner";
import { supabase } from "../lib/supabase";

type SendState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; email: string }
  | { kind: "error"; message: string };

/**
 * Sign-in surface. Magic-link only — no passwords. Lives outside the AppShell
 * so the layout is fully bespoke: centered card on a mesh-gradient background
 * with the brand mark above. The card itself uses the `.glass` treatment so
 * the gradient bleeds through subtly.
 */
export function Login() {
  const [params] = useSearchParams();
  const prefill = params.get("email")?.trim() ?? "";
  const [email, setEmail] = useState(prefill);
  const [state, setState] = useState<SendState>({ kind: "idle" });

  useEffect(() => {
    if (prefill && !email) setEmail(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setState({ kind: "sending" });
    const { error } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: { emailRedirectTo: window.location.origin + "/" },
    });
    if (error) {
      setState({ kind: "error", message: error.message });
    } else {
      setState({ kind: "sent", email: trimmed });
    }
  }

  return (
    <main className="relative grid min-h-screen place-items-center px-4 py-10 sm:px-6">
      {/* Ambient mesh behind the card. Lives outside the card so it can be huge. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 mesh-hero opacity-90"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink-0/80"
      />

      <div className="relative w-full max-w-sm motion-safe:animate-fade-up">
        <div className="flex items-center justify-center">
          <div
            aria-hidden="true"
            className="grid h-14 w-14 place-items-center rounded-xl
                       bg-gradient-to-br from-accent to-accent-press
                       text-ink-0 shadow-glow-accent"
          >
            <span className="text-2xl font-bold">S</span>
          </div>
        </div>
        <h1 className="display mt-6 text-center text-3xl tracking-tight text-fg-0">
          Yes&amp; Scheduler
        </h1>
        <p className="mt-2 text-center text-sm text-fg-2">
          Sign in with a magic link sent to your email.
        </p>

        <div className="mt-8 rounded-xl glass p-6 motion-safe:animate-fade-up-2">
          {state.kind === "sent" ? (
            <div className="motion-safe:animate-fade-in">
              <div
                className="grid h-12 w-12 place-items-center rounded-full
                           border border-accent-ok/40 bg-accent-ok/10 text-accent-ok
                           shadow-glow-ok"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <p className="mt-4 text-base font-semibold text-fg-0">
                Check your inbox
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-fg-1">
                We sent a sign-in link to{" "}
                <span className="font-medium text-accent">{state.email}</span>.
                Click it within an hour to finish signing in.
              </p>
              <button
                type="button"
                onClick={() => setState({ kind: "idle" })}
                className="mt-5 text-sm font-medium text-accent transition-colors hover:text-accent-hover"
              >
                Use a different email →
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-fg-1"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  enterKeyHint="send"
                  autoFocus
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="field mt-1.5 text-base"
                />
              </div>

              {state.kind === "error" ? (
                <p
                  role="alert"
                  className="rounded-md border border-accent-bad/30 bg-accent-bad/10
                             px-3 py-2 text-sm text-accent-bad
                             motion-safe:animate-fade-in"
                >
                  {state.message}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={state.kind === "sending"}
                className="btn-primary w-full h-11 text-base"
              >
                {state.kind === "sending" ? (
                  <>
                    <Spinner />
                    Sending…
                  </>
                ) : (
                  "Send magic link"
                )}
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-fg-3">
          Trouble signing in? Ask your manager.
        </p>
      </div>
    </main>
  );
}
